/* global Stripe */
import { Controller } from "@hotwired/stimulus";
import config from "@app/config";
import Rails from "@rails/ujs";

let stripe;

try {
  stripe = Stripe(config.stripePublicKey);
} catch (_e) {
  console.log("Stripe is not defined");
}

export default class extends Controller {
  static targets = [
    "paymentIntentInfo",
    "cardNumberElement",
    "cardExpiryElement",
    "cardCvcElement",
    "auBankAccountElement",
    "paymentSelection",
    "stripeContainer",
    "submitButton",
    "paymentMessage",
    "name",
    "email",
  ];

  static values = {
    returnUrl: String,
  };

  connect() {
    this.initializeStripe();
    this.addEventListeners();
  }

  initializeStripe() {
    if (stripe === undefined) return;

    const style = {
      base: {
        fontSize: "18px",
        fontFamily: "Open Sans, sans-serif",
        fontWeight: 300,
        "::placeholder": {
          backgroundColor: "#fff",
        },
      },
    };

    const cardOptions = {
      style,
      disabled: false,
      hideIcon: false,
      iconStyle: "default",
    };
    
    const bankOptions = {
      style,
      disabled: false,
    };

    const elements = stripe.elements();

    if (this.hasCardNumberElementTarget) {
      this.cardNumber = elements.create("cardNumber", cardOptions);
      this.cardNumber.mount(this.cardNumberElementTarget);
      
      this.cardExpiry = elements.create("cardExpiry", cardOptions);
      this.cardExpiry.mount(this.cardExpiryElementTarget);
      
      this.cardCvc = elements.create("cardCvc", cardOptions);
      this.cardCvc.mount(this.cardCvcElementTarget);
    }

    if (this.hasAuBankAccountElementTarget) {
      this.auBankAccount = elements.create("auBankAccount", bankOptions);
      this.auBankAccount.mount(this.auBankAccountElementTarget);
    }
  }

  addEventListeners() {
    this.paymentSelectionTargets.forEach((input) => {
      input.addEventListener("change", this.handlePaymentSelectionChange.bind(this));
    });
  }

  handlePaymentSelectionChange(event) {
    this.hideAllStripeContainers();

    const selectedValue = event.target.value;

    // Cash on delivery has no Stripe form container
    if (selectedValue === "cod") {
      this.enableSubmitButton();
      return;
    }

    const container = this.stripeContainerTargets.find(
      (stripeContainer) => stripeContainer.id === `${selectedValue}-stripe-container`,
    );

    if (!container) {
      this.disableSubmitButton();
      return;
    }

    this.showContainer(container);
    this.setSubmitState(event.target.id, container);
  }

  hideAllStripeContainers() {
    this.stripeContainerTargets.forEach((container) => {
      container.style.display = "none";
      container.querySelectorAll("input").forEach((input) => {
        input.disabled = true;
      });
    });
  }

  showContainer(container) {
    container.style.display = "block";
    container.querySelectorAll("input").forEach((input) => {
      input.disabled = false;
      if (input.id === "name") {
        input.required = true;
      }
    });
  }

  setSubmitState(selectionId, container) {
    if (selectionId === "credit-card") {
      this.enableSubmitButton();
    } else if (selectionId === "direct-debit") {
      this.attachDirectDebitWatcher(container);
    } else {
      this.disableSubmitButton();
    }
  }

  attachDirectDebitWatcher(container) {
    const nameInput = container.querySelector("#name");

    const toggleSubmit = () => {
      if (!nameInput || nameInput.value.trim().length === 0) {
        this.disableSubmitButton();
      } else {
        this.enableSubmitButton();
      }
    };

    toggleSubmit();

    if (nameInput && !nameInput.dataset.submitListenerAttached) {
      nameInput.addEventListener("input", toggleSubmit);
      nameInput.dataset.submitListenerAttached = "true";
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false;
    }
  }

  disableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true;
    }
  }

  async handleSubmit(event) {
    event.preventDefault();
    this.disableSubmitButton();

    const selectedInput = this.paymentSelectionTargets.find((input) => input.checked);
    if (!selectedInput) return;

    const selectedValue = selectedInput.value;

    if (selectedValue === "cod") {
      await this.handleCodPayment(selectedInput);
    } else {
      await this.handleStripePayment(selectedValue);
    }
  }

  async handleCodPayment(selectedInput) {
    const codUrl = selectedInput.dataset.url + ".json";

    Rails.ajax({
      url: codUrl,
      type: "POST",
      success: (_data) => {
        window.location.href = this.returnUrlValue;
      },
      error: (data) => {
        this.enableSubmitButton();
        this.showMessage(data.errors[0].detail);
      },
    });
  }

  async handleStripePayment(selectedValue) {
    const clientSecret = this.paymentIntentInfoTarget.dataset.clientSecret;
    const nameValue = this.hasNameTarget ? this.nameTarget.value : "";
    const emailValue = this.hasEmailTarget ? this.emailTarget.value : "";

    const { error } = await (selectedValue === "debit"
      ? stripe.confirmAuBecsDebitPayment(clientSecret, {
          payment_method: {
            au_becs_debit: this.auBankAccount,
            billing_details: {
              name: nameValue,
              email: emailValue,
            },
          },
          return_url: this.returnUrlValue,
        })
      : stripe.confirmCardPayment(clientSecret, {
          payment_method: {
            card: this.cardNumber,
            billing_details: {
              name: nameValue,
            },
          },
          return_url: this.returnUrlValue,
        }));

    this.enableSubmitButton();

    if (error) {
      this.resetSubmitLoading();
      this.showMessage(error.message);
    } else {
      window.HSOverlay.open("#modal-purchase_complete");
      setTimeout(() => {
        window.location.href = this.returnUrlValue;
      }, 2000);
    }
  }

  resetSubmitLoading() {
    if (!this.hasSubmitButtonTarget) return;

    const spinnerElement = this.submitButtonTarget.querySelector("[name='spinner']");
    const iconElement = this.submitButtonTarget.querySelector("svg");

    spinnerElement?.classList.add("hidden");
    iconElement?.classList.remove("hidden");
  }

  showMessage(messageText) {
    this.resetSubmitLoading();

    if (this.hasPaymentMessageTarget) {
      this.paymentMessageTarget.classList.remove("hidden");
      this.paymentMessageTarget.classList.add("inline-block");
      this.paymentMessageTarget.textContent = messageText;

      setTimeout(() => {
        this.paymentMessageTarget.classList.add("hidden");
        this.paymentMessageTarget.textContent = "";
      }, 4000);
    }
  }
}
